import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useWhales } from '../lib/useWhales.js';
import type { Whale } from '@gilding/shared';
import { whaleImgUrl } from '../api/client.js';
import { cn } from '../lib/classnames.js';
import { useQueuedImage } from '../lib/imageQueue.js';
import { FoldedCorner } from './FoldedCorner.js';
import { trackProduct } from '../lib/productMetrics.js';

interface WhalePickerProps {
  selected: Whale | null;
  onChange: (whale: Whale | null) => void;
  /**
   * When true (e.g. the locked share/drop page), there is no "gild from text"
   * fallback — a character is required — so the "Gild from text instead" clear
   * action and the no-whales text-fallback copy are suppressed.
   */
  lockToWhale?: boolean;
}

/**
 * Whale picker. Title becomes a real `.heading-section` (no uppercase mini-cap),
 * a clear-selection action lives as a `.btn-ghost`, and the active tile uses
 * the shared folded-gold-corner active state — no full opaque "selected" pill.
 */
export function WhalePicker({ selected, onChange, lockToWhale = false }: WhalePickerProps) {
  const whalesQuery = useWhales();

  if (whalesQuery.isLoading) {
    return (
      <section className="flex flex-col gap-3">
        <PickerHeader count={null} hasSelection={false} onClear={() => {}} />
        {/* Spotlight placeholder */}
        <div className="flex flex-col gap-4 rounded-md border border-hairline-strong bg-surface-elevated p-3 shadow-paper sm:flex-row sm:p-4">
          <div className="sweep mx-auto aspect-square w-40 shrink-0 rounded-sm border border-hairline/60 bg-surface-sunken/60 sm:mx-0 sm:w-44" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="sweep h-5 w-40 rounded-xs bg-surface-sunken/60" />
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="sweep h-9 rounded-xs bg-surface-sunken/60" />
              ))}
            </div>
          </div>
        </div>
        <WhaleGrid>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="sweep aspect-square w-full rounded-sm border border-hairline/60 bg-surface-sunken/60" />
          ))}
        </WhaleGrid>
        <p className="font-display italic text-caption text-ink-subtle">
          Looking up the whales in your wallet.
        </p>
      </section>
    );
  }

  if (whalesQuery.isError && !whalesQuery.data) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">Your characters</h2>
        <div className="rounded-sm border border-danger/30 bg-danger/[0.05] px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col">
              <p className="font-display text-[1.0625rem] font-medium text-danger">
                The indexer didn&apos;t answer
              </p>
              <p className="mt-1 font-display italic text-caption text-ink-soft">
                We can&apos;t see your whales just now. Gild from text below, or try again.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void whalesQuery.refetch()}
              disabled={whalesQuery.isFetching}
              className="btn-ghost"
            >
              {whalesQuery.isFetching ? 'Trying again…' : 'Try again'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const whales = whalesQuery.data?.whales ?? [];

  if (whales.length === 0) {
    // No whales in the wallet: outside the locked drop page there's nothing to
    // pick and a stand-in character steps in silently, so the picker disappears
    // entirely — no header, no "no Whacky Whales" note. Only the locked page,
    // which genuinely requires a whale, still says one is needed.
    if (!lockToWhale) return null;
    return (
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">Your characters</h2>
        <p className="max-w-prose font-display italic text-lede text-ink-soft">
          No Whacky Whales sit in this wallet. You need one to make this scene yours.
        </p>
      </section>
    );
  }

  return (
    <div className="min-w-0">
    {(whalesQuery.isFetching || whalesQuery.isError) && <p role="status" className="mb-3 font-display text-caption text-ink-soft">{whalesQuery.isError ? 'Showing your saved pod. Live ownership could not be refreshed.' : 'Your saved pod is ready. Checking for changes…'}{whalesQuery.isError && <button className="btn-ghost ml-2" onClick={() => void whalesQuery.refetch()}>Retry refresh</button>}</p>}
    <WhaleGallery
      whales={whales}
      selected={selected}
      onChange={onChange}
      lockToWhale={lockToWhale}
    />
    </div>
  );
}

/**
 * WhaleGallery — the pod browsed as a museum wall + spotlight inspector.
 *
 *  - SPOTLIGHT: the active whale (the one you're hovering, else the one you've
 *    selected) shown large in a gilded frame, with a brass-plate name, token #,
 *    and its traits laid out like a placard — so browsing means actually reading
 *    your characters, not squinting at thumbnails.
 *  - WALL: every portrait framed; hover lifts the frame and slides up a nameplate,
 *    the selected one wears the gold corner. Search filters a large pod by name
 *    or token number. Capped height + native vertical scroll.
 */
interface TraitFilter {
  type: string;
  value: string;
}
// Token number is the only ordering that means anything for an NFT pod; the
// only choice is direction. (Alphabetical was dropped — names aren't meaningful
// to sort a collection by.)
type SortKey = 'asc' | 'desc';

function WhaleGallery({
  whales,
  selected,
  onChange,
  lockToWhale,
}: {
  whales: Whale[];
  selected: Whale | null;
  onChange: (whale: Whale | null) => void;
  lockToWhale: boolean;
}) {
  const [hovered, setHovered] = useState<Whale | null>(null);
  const [pinned, setPinned] = useState<Whale | null>(null);
  const [query, setQuery] = useState('');
  const [traitFilter, setTraitFilter] = useState<TraitFilter | null>(null);
  const [sort, setSort] = useState<SortKey>('asc');
  // A real tap selects on the FIRST press on touch (no more two-tap): we select
  // on pointer-up and suppress the click it emits. `tapRef` tracks the press so
  // a scroll-drag (finger moved) doesn't count as a tap.
  const tapRef = useRef<{ id: number; x: number; y: number; t: number } | null>(null);
  const lastPointerSelectRef = useRef(0);
  // Windowing: large wallets (hundreds of whales) tank mobile if every tile is
  // mounted at once. Render a batch, grow it only as the picker is scrolled.
  const PAGE = 30;
  const [visible, setVisible] = useState(PAGE);

  const filtered = useMemo(() => {
    let list = whales;
    if (traitFilter) {
      list = list.filter((w) =>
        w.traits.some((t) => t.type === traitFilter.type && t.value === traitFilter.value),
      );
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (w) => w.name.toLowerCase().includes(q) || String(w.tokenId).includes(q),
      );
    }
    return [...list].sort((a, b) =>
      sort === 'desc' ? b.tokenId - a.tokenId : a.tokenId - b.tokenId,
    );
  }, [whales, traitFilter, query, sort]);

  // What the spotlight shows: the portrait under the cursor wins; then a random
  // "pinned" pick; then the chosen character; then the first of the filtered pod.
  const active = hovered ?? pinned ?? selected ?? filtered[0] ?? whales[0] ?? null;
  const showTools = whales.length > 6;

  const onTraitClick = (type: string, value: string): void => {
    setTraitFilter((cur) => (cur?.type === type && cur.value === value ? null : { type, value }));
    setPinned(null);
  };
  const surprise = (): void => {
    const pool = filtered.length ? filtered : whales;
    if (pool.length === 0) return;
    // vary the index source without Math.random reliance on any single call
    const idx = Math.floor((performance.now() % 997) / 997 * pool.length) % pool.length;
    setPinned(pool[idx] ?? null);
    setHovered(null);
  };

  // Re-window from the top whenever the filter/sort changes the result set.
  const filterKey = `${query}|${sort}|${traitFilter?.type ?? ''}:${traitFilter?.value ?? ''}`;
  useEffect(() => {
    setVisible(PAGE);
  }, [filterKey]);
  const shown = filtered.slice(0, visible);
  const loadMore = useCallback(() => {
    setVisible((v) => (v < filtered.length ? v + PAGE : v));
  }, [filtered.length]);

  return (
    <section className="flex flex-col gap-3">
      <PickerHeader
        count={whales.length}
        hasSelection={!!selected}
        onClear={() => onChange(null)}
        lockToWhale={lockToWhale}
      />

      {/* Persistent confirmation of the chosen character — always visible once
          picked, independent of what the spotlight is previewing, so a scroll-
          hover never makes the selection look lost. */}
      {selected && (
        <div className="flex items-center gap-3 rounded-sm border border-accent/45 bg-accent/[0.07] px-3 py-2">
          <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-xs border border-accent/40">
            <img
              src={whaleImgUrl(selected.thumbnailUrl ?? selected.imageUrl)}
              alt=""
              className="h-full w-full object-cover"
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-caption text-ink">
              <span className="text-accent-deep">Your character · </span>
              {selected.name}
            </p>
            <p className="font-mono text-[0.625rem] text-ink-subtle">#{selected.tokenId}</p>
          </div>
          {!lockToWhale && (
            <button type="button" className="btn-ghost shrink-0" onClick={() => onChange(null)}>
              Clear
            </button>
          )}
        </div>
      )}

      <Spotlight
        whale={active}
        isSelected={!!active && selected?.tokenId === active.tokenId}
        lockToWhale={lockToWhale}
        traitFilter={traitFilter}
        onTraitClick={onTraitClick}
        onUse={() => active && onChange(active)}
      />

      {showTools && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name or #number"
              aria-label="Filter your pod"
              className="w-full rounded-sm border border-hairline bg-surface-sunken/50 py-2 pl-9 pr-3 font-display text-caption text-ink placeholder:text-ink-subtle focus:border-accent/60 focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() => setSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-hairline px-3 py-2 font-display text-caption text-ink-soft transition-colors duration-200 hover:border-accent/55 hover:text-ink"
            title="Toggle order: low ↔ high token number"
          >
            <SortIcon className="h-3.5 w-3.5" />
            {sort === 'asc' ? 'Low # first' : 'High # first'}
          </button>
          <button
            type="button"
            onClick={surprise}
            aria-label="Spotlight a random whale"
            title="Surprise me"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-hairline text-ink-soft transition-colors duration-200 hover:border-accent/55 hover:text-accent-deep"
          >
            <DiceIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {traitFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display italic text-caption text-ink-subtle">
            {filtered.length} {filtered.length === 1 ? 'whale shares' : 'whales share'}
          </span>
          <button
            type="button"
            onClick={() => setTraitFilter(null)}
            className="inline-flex items-center gap-1.5 rounded-full border border-accent/50 bg-accent/10 px-2.5 py-1 font-display text-caption text-accent-deep transition-colors duration-200 hover:bg-accent/20"
          >
            <span className="text-[0.625rem] uppercase tracking-[0.12em] text-ink-subtle">
              {traitFilter.type}
            </span>
            {traitFilter.value}
            <span aria-hidden="true" className="text-ink-subtle">
              ✕
            </span>
          </button>
        </div>
      )}

      <WhaleGrid onReachEnd={loadMore} hasMore={visible < filtered.length}>
        {shown.map((w) => (
          <PodTile
            key={w.tokenId}
            whale={w}
            isSelected={selected?.tokenId === w.tokenId}
            isActive={active?.tokenId === w.tokenId}
            onChange={onChange}
            onHover={setHovered}
            tapRef={tapRef}
            lastPointerSelectRef={lastPointerSelectRef}
          />
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full py-6 text-center font-display italic text-caption text-ink-subtle">
            No whale matches your filter.
          </p>
        )}
      </WhaleGrid>
      <p className="font-display italic text-caption text-ink-subtle">
        {visible < filtered.length
          ? `Showing ${visible} of ${filtered.length} — scroll for more.`
          : 'Hover a portrait to inspect it; tap to choose. Click a trait to find its kin.'}
      </p>
    </section>
  );
}

/**
 * One portrait in the wall. The image is served through the caching proxy and
 * its load is paced by a global queue (so a large pod doesn't burst-fail at the
 * upstream), then blooms in. A failed proxied thumbnail falls back to the full
 * image, and a tile is never left invisibly stuck.
 */
function PodTile({
  whale: w,
  isSelected,
  isActive,
  onChange,
  onHover,
  tapRef,
  lastPointerSelectRef,
}: {
  whale: Whale;
  isSelected: boolean;
  isActive: boolean;
  onChange: (w: Whale | null) => void;
  onHover: (updater: Whale | null | ((h: Whale | null) => Whale | null)) => void;
  tapRef: MutableRefObject<{ id: number; x: number; y: number; t: number } | null>;
  lastPointerSelectRef: MutableRefObject<number>;
}) {
  // Grid uses the light thumbnail (full imageUrl stays for spotlight + gen),
  // both routed through the proxy; the queue paces when the load actually
  // starts.
  const [attempt, setAttempt] = useState(0);
  const loadStarted = useRef(Date.now());
  const recorded = useRef(false);
  const [failed, setFailed] = useState(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (retryTimer.current) clearTimeout(retryTimer.current); }, []);
  const initial = whaleImgUrl(w.thumbnailUrl ?? w.imageUrl);
  const full = whaleImgUrl(w.imageUrl);
  const source = attempt === 0 ? initial : attempt === 1 ? full : full ? full + '&retry=' + attempt : undefined;
  const { src, onSettled } = useQueuedImage(source, () => imageError());
  const retry = (): void => { setFailed(false); setAttempt(0); };
  const imageError = (): void => {
    onSettled();
    if (attempt >= 3) { setFailed(true); trackProduct('portrait.failed', Date.now() - loadStarted.current); return; }
    retryTimer.current = setTimeout(() => setAttempt((n) => n + 1), attempt === 0 ? 0 : 800 * attempt);
  };
  return (
    <button
      type="button"
      onPointerDown={(e) => {
        tapRef.current = { id: w.tokenId, x: e.clientX, y: e.clientY, t: performance.now() };
      }}
      onPointerUp={(e) => {
        const d = tapRef.current;
        tapRef.current = null;
        if (!d || d.id !== w.tokenId) return;
        const moved = Math.hypot(e.clientX - d.x, e.clientY - d.y);
        // A genuine tap (finger barely moved) — select on this first press.
        if (moved < 12 && performance.now() - d.t < 800) {
          lastPointerSelectRef.current = performance.now();
          if (failed) retry();
          else onChange(isSelected ? null : w);
        }
      }}
      onPointerCancel={() => {
        tapRef.current = null;
      }}
      onClick={() => {
        // Pointer taps are handled on pointer-up (so mobile selects in one
        // press); swallow the click they emit so it can't toggle twice.
        // Keyboard Enter/Space emits a click with no prior pointer-select.
        if (performance.now() - lastPointerSelectRef.current < 700) return;
        if (failed) retry();
        else onChange(isSelected ? null : w);
      }}
      onMouseEnter={() => onHover(w)}
      onMouseLeave={() => onHover((h) => (h?.tokenId === w.tokenId ? null : h))}
      onFocus={() => onHover(w)}
      onBlur={() => onHover((h) => (h?.tokenId === w.tokenId ? null : h))}
      aria-pressed={isSelected}
      aria-label={failed ? `Retry portrait for ${w.name}` : `Pick ${w.name}, token ${w.tokenId}`}
      title={w.name}
      className={cn(
        'group relative block w-full self-start overflow-hidden rounded-sm border bg-surface-sunken outline-none transition-all duration-200',
        'hover:-translate-y-0.5 hover:shadow-lift focus-visible:-translate-y-0.5 focus-visible:shadow-lift',
        isSelected
          ? 'border-accent ring-2 ring-accent/40'
          : isActive
            ? 'border-accent/60'
            : 'border-hairline/60 hover:border-accent/55',
      )}
    >
      {/* The IMAGE is the square sizer (replaced element honours aspect-ratio
          reliably); badges overlay it absolutely. A fixed aspect-square box
          holds the tile shape before the src is granted, so the grid never
          reflows as images stream in. */}
      <span className="block aspect-square w-full">
        {failed && <span className="flex h-full items-center justify-center px-2 text-center text-caption text-ink-soft">Tap to retry portrait</span>}
        {src && !failed && (
          <img
            key={src}
            src={src}
            alt=""
            decoding="async"
            ref={(el) => {
              if (el?.complete && el.naturalWidth > 0) {
                el.classList.add('is-loaded');
                onSettled();
              }
            }}
            onLoad={(e) => {
              if (!recorded.current) { recorded.current = true; trackProduct('portrait.loaded', Date.now() - loadStarted.current); }
              e.currentTarget.classList.add('is-loaded');
              onSettled();
            }}
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
              imageError();
            }}
            className={cn(
              'block h-full w-full object-cover ease-page',
              'opacity-0 transition-[opacity,transform] duration-500 [&.is-loaded]:opacity-100',
              'group-hover:scale-105 group-focus-visible:scale-105',
              isSelected && 'scale-[1.03]',
            )}
          />
        )}
      </span>

      {/* Token number — a quiet gilded plate, always legible. */}
      <span className="absolute left-1 top-1 rounded-full bg-black/45 px-1.5 py-0.5 font-mono text-[0.55rem] leading-none text-white/85 backdrop-blur-sm">
        #{w.tokenId}
      </span>

      {/* Name slides up on hover — never covers the character at rest. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/80 via-black/45 to-transparent px-2 pb-1.5 pt-5 text-left transition-transform duration-200 group-hover:translate-y-0 group-focus-visible:translate-y-0">
        <span className="block truncate font-display text-caption text-white">{w.name}</span>
      </span>

      {isSelected && <FoldedCorner size={16} />}
    </button>
  );
}

/** The spotlight inspector — the active whale, large, with its placard. */
function Spotlight({
  whale,
  isSelected,
  lockToWhale,
  traitFilter,
  onTraitClick,
  onUse,
}: {
  whale: Whale | null;
  isSelected: boolean;
  lockToWhale: boolean;
  traitFilter: TraitFilter | null;
  onTraitClick: (type: string, value: string) => void;
  onUse: () => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);

  const tilt = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const el = frameRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(640px) rotateY(${(px * 9).toFixed(2)}deg) rotateX(${(-py * 9).toFixed(2)}deg) scale(1.02)`;
  };
  const untilt = (): void => {
    const el = frameRef.current;
    if (el) el.style.transform = 'perspective(640px) rotateY(0deg) rotateX(0deg) scale(1)';
  };

  if (!whale) {
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed border-hairline px-4 py-10 text-center">
        <p className="font-display italic text-caption text-ink-subtle">
          Hover a portrait below to inspect it.
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-4 overflow-hidden rounded-md border border-hairline-strong bg-surface-elevated p-3 shadow-paper sm:min-h-[15rem] sm:flex-row sm:p-4">
      {/* Top gild rule — a hairline of leaf across the exhibit. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: 'linear-gradient(90deg,transparent,rgba(233,200,115,0.6),transparent)' }}
      />
      {/* Gilded mat — a soft gold glow behind the featured portrait. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-12 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full opacity-70 blur-2xl"
        style={{ background: 'radial-gradient(closest-side, rgba(233,200,115,0.24), transparent 70%)' }}
      />
      <div
        ref={frameRef}
        onPointerMove={tilt}
        onPointerLeave={untilt}
        className="relative mx-auto aspect-square w-44 shrink-0 overflow-hidden rounded-sm border border-accent/45 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)] ring-1 ring-inset ring-white/10 transition-transform duration-300 ease-out [transform-style:preserve-3d] sm:mx-0 sm:w-52"
      >
        <img
          key={whale.tokenId}
          src={whaleImgUrl(whale.imageUrl)}
          alt={whale.name}
          className="spotlight-fade h-full w-full object-cover"
        />
        {isSelected && <FoldedCorner size={22} />}
      </div>

      <div className="relative flex min-w-0 flex-1 flex-col">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="heading-section truncate">{whale.name}</h3>
          <span className="shrink-0 rounded-full border border-hairline bg-surface-sunken/60 px-2 py-0.5 font-mono text-[0.6875rem] text-ink-soft">
            #{whale.tokenId}
          </span>
        </div>

        {/* gilt divider */}
        <div
          aria-hidden="true"
          className="mb-3 mt-2.5 h-px w-full"
          style={{ background: 'linear-gradient(90deg,rgba(233,200,115,0.5),rgba(180,150,90,0.18) 45%,transparent)' }}
        />

        {whale.traits.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {whale.traits.map((t) => {
              const on = traitFilter?.type === t.type && traitFilter.value === t.value;
              return (
                <button
                  type="button"
                  key={`${t.type}-${t.value}`}
                  onClick={() => onTraitClick(t.type, t.value)}
                  title={`Find whales with ${t.type}: ${t.value}`}
                  className={cn(
                    'group/chip min-w-0 rounded-xs border px-2.5 py-1.5 text-left transition-all duration-150',
                    on
                      ? 'border-accent/70 bg-accent/10 shadow-[0_0_0_1px_rgba(233,200,115,0.25)]'
                      : 'border-hairline/60 bg-surface-sunken/50 hover:-translate-y-px hover:border-accent/50 hover:bg-accent/[0.07]',
                  )}
                >
                  <p className="truncate text-[0.625rem] uppercase tracking-[0.14em] text-ink-subtle">
                    {t.type}
                  </p>
                  <p className={cn('truncate font-display text-caption', on ? 'text-accent-deep' : 'text-ink')}>
                    {t.value}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="font-display italic text-caption text-ink-subtle">
            No traits indexed for this token.
          </p>
        )}

        {/* Fixed-height action row — never resizes the card as you browse. */}
        {!lockToWhale && (
          <div className="mt-auto flex h-9 items-center pt-4">
            {isSelected ? (
              <span className="inline-flex items-center gap-1.5 font-display text-caption text-accent-deep">
                <CheckIcon className="h-4 w-4" />
                This is your character
              </span>
            ) : (
              <button
                type="button"
                onClick={onUse}
                className="inline-flex h-8 items-center gap-2 rounded-full border border-accent/50 px-4 font-display text-caption text-ink transition-colors duration-200 hover:bg-accent/10 hover:text-accent-deep"
              >
                Use this character
                <span aria-hidden="true">&rarr;</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" className={className}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5 14 14" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" className={className}>
      <path d="M3 8.5 6.5 12 13 4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SortIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d="M4 3v10M4 13l-2-2M4 13l2-2" />
      <path d="M9 4h5M9 8h4M9 12h2" />
    </svg>
  );
}

function DiceIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true" className={className}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2.5" />
      <circle cx="5.5" cy="5.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="5.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Wrapping grid in a height-capped, vertically-scrolling container. Replaces the
 * old single-row horizontal rail: a standard mouse wheel scrolls it naturally
 * (a horizontal rail ignored the wheel), and many more characters are visible at
 * once. The cap keeps the picker's footprint constant no matter how large the
 * wallet's pod is; a bottom fade hints there's more below.
 */
function WhaleGrid({
  children,
  onReachEnd,
  hasMore = false,
}: {
  children: ReactNode;
  onReachEnd?: () => void;
  hasMore?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const cbRef = useRef(onReachEnd);
  cbRef.current = onReachEnd;

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) cbRef.current?.();
      },
      { root, rootMargin: '240px 0px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
    // Re-arm after each page grows so the next batch can load in turn.
  }, [hasMore, children]);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        data-lenis-prevent
        // marquee-rail hides the scrollbar + adds momentum; the grid wraps and the
        // container scrolls vertically once it exceeds the cap.
        className="marquee-rail grid max-h-[19rem] min-w-0 touch-pan-y grid-cols-4 items-start gap-2.5 overflow-x-hidden overflow-y-auto overscroll-contain px-1 pb-2 pt-1 sm:grid-cols-5 md:grid-cols-6"
        role="listbox"
        aria-label="Pick a character"
      >
        {children}
        {/* Sentinel — when it scrolls into view, the next batch mounts. */}
        {hasMore && <div ref={sentinelRef} aria-hidden="true" className="col-span-full h-px" />}
      </div>
      {/* Bottom fade — affordance that the pod continues below the fold. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-7 bg-gradient-to-t from-surface to-transparent"
      />
    </div>
  );
}

function PickerHeader({
  count,
  hasSelection,
  onClear,
  lockToWhale = false,
}: {
  count: number | null;
  hasSelection: boolean;
  onClear: () => void;
  lockToWhale?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <div className="flex items-baseline gap-3">
        <h2 className="heading-section">Pick your character</h2>
        {count !== null && (
          <span className="font-display italic text-caption text-ink-subtle">
            {count === 1 ? 'one whale in the wallet' : `${count} whales in the wallet`}
          </span>
        )}
      </div>
      {hasSelection && !lockToWhale && (
        <button type="button" className="btn-ghost" onClick={onClear}>
          Gild from text instead
        </button>
      )}
    </div>
  );
}
